import React, { useEffect } from 'react';

import {
  apstagAttributes,
  supportedMerchant,
} from '../../../../sharedModules/apstag/getAttributes';
import { addAsinToBidStore } from '../../../../sharedModules/apstag/integration';
import { isServerside } from '../../../utils/isServerside';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetContext';

import type { TrackedLinkProps } from './model';
import { TargetEnum } from './model';
import { addNcaAttributes, getTarget } from './modules/attribute';

const TrackedLink: React.FC<TrackedLinkProps> = ({
  children,
  customTarget,
  attributes,
  trackLinkClick,
}) => {
  const middleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
    // Track the click if it is a middle click
    // middle clicks don't fire onClick or onContextMenu events
    if (typeof event.button !== 'undefined' && event.button === 1 && trackLinkClick) {
      trackLinkClick(event);
    }
  };

  const click = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
    // Track the click if it is not a middle click
    // middle clicks don't fire onClick or onContextMenu events
    // Added to ensure Firefox doesn't fire both click events
    if (typeof event.button !== 'undefined' && event.button !== 1 && trackLinkClick) {
      trackLinkClick(event);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();

    if (typeof event.key !== 'undefined' && event.code === 'Enter' && trackLinkClick) {
      trackLinkClick(event);
    }
  };

  const { translate, site } = useHawkWidgetContext();

  // What data props are passed into this component are unknown/can change
  // as such the only consistent way to know if the link might be for Amazon
  // is if the url contains the word amazon
  const updatedAttributes =
    attributes.href?.includes(supportedMerchant) && !isServerside()
      ? addNcaAttributes(attributes)
      : attributes;

  useEffect(() => {
    if (updatedAttributes[apstagAttributes.asin]) {
      addAsinToBidStore(updatedAttributes[apstagAttributes.asin]);
    }
  }, [updatedAttributes]);

  const target =
    site.hostname && attributes.href && getTarget(site.hostname, updatedAttributes.href);

  return (
    <a
      onClick={click}
      onContextMenu={click}
      onMouseDown={middleClick}
      data-google-interstitial="false"
      aria-label={translate('defaultLinkText')}
      {...updatedAttributes}
      // add 'sponsored noopener' to external links
      {...(target === TargetEnum.BLANK ? { rel: 'sponsored noopener' } : {})}
      target={customTarget ?? target}
      onKeyDown={onKeyDown}
      role="link"
      tabIndex={0}
    >
      {children}
    </a>
  );
};

export default TrackedLink;
